import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Option, RenderIf } from 'react-rainbow-components';
import { useOpenModal } from '@rainbow-modules/hooks';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import Picklist from 'components/Picklist';
import { Respondermapping } from 'data/firestore/agent/respondermapping/types';
import useManageResponderMapping from 'data/hooks/useManageResponderMapping';
import { ADD_EDIT_LLM_RESPONDER_MODAL } from '../../../constants';
import { CellWrapper } from './styled';
import context, { Context } from '../context';

interface Props {
    row?: Record<string, unknown>;
    agentId: string;
    disabled?: boolean;
    allowAgents?: boolean;
}

const AIPersonaColumn = ({
    row,
    agentId,
    disabled = false,
    allowAgents = false,
}: Props) => {
    const { responders, responderMapping, automations = [] } = useContext<Context>(context);
    const navigate = useNavigate();

    const [openModal] = useOpenModal(ADD_EDIT_LLM_RESPONDER_MODAL);

    const {
        create: createMapping,
        update: updateMapping,
        delete: deleteMapping,
    } = useManageResponderMapping(agentId);

    const respondersList = useMemo(
        () => responders?.filter(
            (responder) => responder.featureSchema === row?.featureSchema,
        ).map(
            (responder) => (
                <Option
                    key={responder.id}
                    name={responder.id}
                    label={responder.name}
                    value={{
                        type: 'responder',
                        id: responder.id,
                    }}
                    searchableText={responder.name}
                />
            ),
        ),
        [responders, row?.featureSchema],
    );

    const automationsList = useMemo(
        () => automations?.map(
            (automation) => (
                <Option
                    key={automation.id}
                    name={automation.id}
                    label={automation.name}
                    value={{
                        type: 'workflow',
                        id: automation.id,
                    }}
                    searchableText={automation.name}
                />
            ),
        ),
        [automations],
    );

    const value = useMemo(() => {
        const mapping = responderMapping?.find(
            (responderMap) => responderMap.feature === row?.name,
        );
        if (mapping) {
            // Legacy mappings omit type; treat missing type + responderId as responder
            const isResponderMapping = mapping.type === 'responder'
                || (!mapping.type && 'responderId' in mapping);
            if (isResponderMapping && 'responderId' in mapping) {
                const featureResponder = responders?.find(
                    (responder) => responder.id === mapping.responderId,
                );
                if (featureResponder) {
                    return {
                        name: featureResponder.id,
                        label: featureResponder.name,
                        value: {
                            type: 'responder',
                            id: featureResponder.id,
                        },
                    };
                }
            }
            if (mapping.type === 'workflow' && 'workflowId' in mapping) {
                const automationResponder = automations?.find(
                    (automation) => automation.id === mapping.workflowId,
                );
                if (automationResponder) {
                    return {
                        name: automationResponder.id,
                        label: automationResponder.name,
                        value: {
                            type: 'workflow',
                            id: automationResponder.id,
                        },
                    };
                }
            }
        }

        return undefined;
    }, [automations, responderMapping, responders, row?.name]);

    const updateResponder = useCallback(
        async ({
            type = 'responder',
            targetId,
        }: {
            type: Respondermapping['type'];
            targetId: string;
        }) => {
            const mapping = responderMapping?.find(
                (responderMap) => responderMap.feature === row?.name,
            );
            if (!mapping) {
                await createMapping({
                    type,
                    feature: row?.name as string,
                    featureSchema: row?.featureSchema as string,
                    targetId,
                });
                return;
            }
            await updateMapping({
                type,
                mappingId: mapping.id,
                targetId,
            });
        },
        [createMapping, responderMapping, row, updateMapping],
    );

    const handleResponderCreated = useCallback(
        async (responder: Record<string, unknown>) => {
            const { id } = responder;
            await updateResponder({
                type: 'responder',
                targetId: id as string,
            });
            navigate(`/${agentId}/llms/${id}`);
        },
        [updateResponder, navigate, agentId],
    );

    const onResponderChange = useCallback(
        async (newValue: PicklistValue) => {
            if (newValue.name === 'new') {
                openModal({
                    initialValues: {
                        skill: 'system',
                        feature: row?.featureSchema,
                    },
                    onSuccess: handleResponderCreated,
                });
                return;
            }

            if (newValue.name === 'none') {
                const mapping = responderMapping?.find(
                    (responderMap) => responderMap.feature === row?.name,
                );
                if (mapping) {
                    await deleteMapping(mapping.id);
                }
                return;
            }

            const { type, id } = newValue.value;
            await updateResponder({
                type: type as Respondermapping['type'],
                targetId: id as string,
            });
        },
        [
            deleteMapping,
            handleResponderCreated,
            openModal,
            responderMapping,
            row?.featureSchema,
            row?.name,
            updateResponder,
        ],
    );

    return (
        <CellWrapper>
            <Picklist
                placeholder="Click to select AI Persona"
                disabled={disabled}
                value={value}
                onChange={onResponderChange}
            >
                <Option name="new" label="Create a Responder" />
                <Option name="none" label="None" />
                <Option variant="header" label="Your responders" />
                {respondersList}
                <RenderIf isTrue={allowAgents}>
                    <Option variant="header" label="Agents" />
                    {automationsList}
                </RenderIf>
            </Picklist>
        </CellWrapper>
    );
};

export default AIPersonaColumn;
